import React, { ReactNode, useEffect, useState } from 'react'
import { useStore } from '@/src/types/store'
import { Box, Typography } from '@mui/material'
import { ErrorIcon, WarningIcon, InfoIcon } from '../social-sharing/components/assets/icons'
import { CrossIcon } from '@/src/assets/icons'
import Button from '../button'

const DISMISSED_BANNER_KEY = 'dismissed_banner_expiry'

const alertStyles: Record<string, string> = {
  error: 'bg-[#F8DEDE]',
  warning: 'bg-[#FCE7D6]',
  info: 'bg-[#D6ECF8]'
}

const alertIcons: Record<string, ReactNode> = {
  error: <ErrorIcon />,
  warning: <WarningIcon />,
  info: <InfoIcon />
}

const WarningPopup = () => {
  const { profile, homeTheme } = useStore()
  const centralMessage = homeTheme?.central_banner

  const [dismissed, setDismissed] = useState(false)
  const [isChecked, setIsChecked] = useState(false)

  useEffect(() => {
    if (!centralMessage?.updated_on) {
      setIsChecked(true)
      return
    }

    const stored = localStorage.getItem(DISMISSED_BANNER_KEY)

    if (!stored) {
      setIsChecked(true)
      return
    }

    try {
      const { expiry, updated_on } = JSON.parse(stored)
      if (updated_on === centralMessage.updated_on && Date.now() < expiry) {
        setDismissed(true)
      } else {
        localStorage.removeItem(DISMISSED_BANNER_KEY)
        setDismissed(false)
      }
    } catch {
      setDismissed(false)
    }
    setIsChecked(true)
  }, [centralMessage?.updated_on])

  if (!centralMessage || dismissed || !isChecked) {
    return null
  }

  if (!profile.first_name && !profile.last_name && !profile.business_name) {
    return null
  }

  if (profile.user_type === 'investor') {
    return null
  }

  const handleDismiss = () => {
    if (!centralMessage?.updated_on) return
    const expiryTime = Date.now() + 4 * 60 * 60 * 1000
    localStorage.setItem(
      DISMISSED_BANNER_KEY,
      JSON.stringify({
        expiry: expiryTime,
        updated_on: centralMessage.updated_on
      })
    )
    setDismissed(true)
  }

  const bgClass = alertStyles[centralMessage.alert_type] || 'bg-status-warning'
  const icon = alertIcons[centralMessage.alert_type] || alertIcons['warning']

  return (
    <Box
      className={`w-full h-12 px-4 py-[6px] ${bgClass} flex items-center gap-2 justify-between sticky top-0 z-50`}
    >
      <Box className="flex items-center gap-2">
        {icon}
        <Typography variant="subtitle1">{centralMessage.message}</Typography>
      </Box>
      <Box className="flex gap-20 items-center">
        {centralMessage?.button_label && (
          <Button
            text={centralMessage.button_label}
            variant="outlined"
            onClick={() => {
              if (centralMessage?.button_url) {
                window.open(centralMessage.button_url, '_blank', 'noopener,noreferrer')
              }
            }}
          />
        )}
        <Box onClick={handleDismiss} className="cursor-pointer">
          <CrossIcon />
        </Box>
      </Box>
    </Box>
  )
}

export default WarningPopup
